import { FloatingChat } from "@/components/floating-chat";
import { Sidebar } from "@/components/sidebar.tsx";
import { WhatsNewModal } from "@/components/changelog/whats-new-modal";
import { WhatsNewPill } from "@/components/changelog/whats-new-pill";
import { useCurrentUser, UserRole } from "@/lib/api/user.ts";
import { APP_ID } from "@/lib/constants.ts";
import { useTimezoneDetection } from "@/lib/hooks/user/use-timezone-detection";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import { SbCallsProvider } from "@/lib/sendbird-calls";
import { SendBirdProvider } from "@sendbird/uikit-react";
import {
  gateByLastActivity,
  LAST_ACTIVITY_STORAGE_KEY,
} from "@/lib/inactivity";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
// import { InactivityMonitor } from "@/components/auth/inactivity-monitor";
import { InactivityMonitor } from "@/components/auth/inactivity-monitor";
import { useFirstMentorMessage } from "@/lib/hooks/conversations/use-first-mentor-message";
import { useEffect } from "react";

export const Route = createFileRoute("/_authenticated")({
  component: () => <AuthenticatedLayout />,
  beforeLoad: async ({ context }) => {
    const isLoginRoute = location.pathname === "/login";

    if (!context.stytchSession?.session_id || !context.user?.id) {
      console.log("Missing session or user, redirecting to login");
      throw redirect({ to: "/login" });
    }

    // Skip authentication factor check in development mode
    if (import.meta.env.VITE_REQUIRE_MFA === "true") {
      if (context.stytchSession?.authentication_factors.length === 1) {
        await context.revokeSession();
        if (!isLoginRoute) throw redirect({ to: "/login" });
      }
    }

    if (context.user.role === UserRole.MENTEE) {
      console.log("User is mentee, revoking session and redirecting");
      await context.revokeSession();
      throw redirect({ to: "/login" });
    }

    if (import.meta.env.VITE_STYTCH_PUBLIC_ENV === "production") {
      const activityGate = gateByLastActivity();
      if (activityGate === "stale") {
        await context.revokeSession();
        localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
        throw redirect({ to: "/login" });
      }
    }

    console.log("Authentication check passed, allowing access");
  },
});

function AuthenticatedLayout() {
  const { data: somethingsUser } = useCurrentUser();
  const setMentorView = useMentorView((state) => state.setMentorView);
  useTimezoneDetection();
  useFirstMentorMessage();

  // If user is a mentor (and not an admin), ensure mentorView is true.
  // This handles the case where a new mentor signs in and mentorView
  // defaults to false in zustand/localStorage.
  const systemRoles = somethingsUser?.systemRoles ?? [];
  const isMentorOnly = somethingsUser?.role === UserRole.MENTOR;
  const isAdministrator =
    somethingsUser?.role === UserRole.SUPERADMIN ||
    somethingsUser?.role === UserRole.ADMIN;
  const hasMentorCapability =
    systemRoles.includes("mentor") || systemRoles.includes("supervisor");

  const mentorView = useMentorView((state) => state.mentorView);

  useEffect(() => {
    if (isMentorOnly) {
      setMentorView(true);
    }
  }, [isMentorOnly, setMentorView]);

  // Reset mentorView if admin doesn't have mentor capability
  // (prevents getting stuck in mentor view from stale localStorage)
  useEffect(() => {
    if (isAdministrator && !hasMentorCapability && mentorView) {
      setMentorView(false);
    }
  }, [isAdministrator, hasMentorCapability, mentorView, setMentorView]);

  if (!somethingsUser) return null;

  return (
    <SbCallsProvider appId={APP_ID}>
      <SendBirdProvider
        appId={APP_ID}
        userId={somethingsUser.id}
        accessToken={somethingsUser.sendbird_access_token}
        uikitOptions={{
          groupChannel: {
            enableVoiceMessage: true,
            enableReactions: true,
            replyType: "quote_reply",
          },
        }}
      >
        <div className="flex">
          <Sidebar />
          <main className="w-screen-sidebar flex-1">
            <Outlet />
            <FloatingChat />
            <WhatsNewPill />
            <WhatsNewModal />
            {import.meta.env.VITE_STYTCH_PUBLIC_ENV === "production" && (
              <InactivityMonitor />
            )}
          </main>
        </div>
      </SendBirdProvider>
    </SbCallsProvider>
  );
}
